import React, { useEffect, useMemo, useRef, useState } from "react";
import { MarkdownRenderer } from "../../../../../components/AIAgents/ChatWithAgent/components/MarkdownRenderer";
import {
  createBlogDraft,
  updateBlogDraft,
  deleteBlogDraft,
  getBlogDraft,
} from "../services/wordpressBlogService";
import {
  listBlogDestinations,
  publishToDestination,
  unpublishFromDestination,
  listAuthors,
  createAuthor,
  previewDraft,
  type BlogAuthor,
  type BlogDestination,
  type HostedBlog,
  type RenderedPreview,
} from "../services/blogPublishingService";
import type {
  BlogPost,
  BlogDraftInput,
  BlogMediaRef,
  BlogPublication,
  BlogStatus,
  WordPressSiteOption,
} from "../types/wordpress.types";
import { ConnectBlogCard } from "./ConnectBlogCard";
import { BlogRichEditor, type BlogRichEditorHandle } from "./BlogRichEditor";
import { fetchMediaByCustomer } from "../../../../../services/contentService";
import { getMediaThumbnailUrl, isImageMediaItem } from "../../../../../types/MediaItem";
import { BlogImagePicker } from "./BlogImagePicker";
import { DeleteIconButton } from "./DeleteIconButton";
import { PublishedPostBanner } from "./PublishedPostBanner";
import { stripDuplicateTitleHeading } from "../utils/blogContent";
import { fetchBrands } from "../../../../../services/brandService";
import ScheduleDateTimeField from "../../../../../components/Shared/ScheduleDateTimeField";
import {
  normalizeScheduledTime,
  formatScheduledForDisplay,
} from "../../../../../utils/scheduleTime";

interface BlogEditorPanelProps {
  customerId: string;
  idToken: string;
  /** Preload an existing draft. When omitted, the panel starts a blank draft. */
  initialDraft?: BlogPost | null;
  /** Unused since SGL-66: the panel loads every destination (WordPress and the
   *  hosted blog) itself. Kept so existing callers still type-check. */
  connectedSites?: WordPressSiteOption[];
  /** While true (and no draft yet), shows a live "writing…" state. */
  loading?: boolean;
  /** Live Markdown streamed from the chat while the blog is being written. */
  streamingPreview?: string;
  /** Which tab the panel opens on once a draft is loaded. Defaults to "edit";
   *  the chat-generation flow passes "preview" to show the styled result first. */
  initialView?: "edit" | "preview";
  onClose: () => void;
  /** Called after a successful publish with the live WordPress URL + post id. */
  onPublished?: (wordpressUrl: string, postId: string) => void;
  /**
   * Called after the draft is persisted (saved or published) with its post id and
   * current published flag, so the caller can remember the saved/published state
   * (e.g. the chat updates its cached draft so reopening shows "Saved"/"Published").
   */
  onPersisted?: (
    postId: string,
    published: boolean,
    wordpressUrl?: string
  ) => void;
}

type BusyAction =
  | "saving"
  | "publishing"
  | "scheduling"
  | "cancelling"
  | "deleting"
  | "unpublishing"
  | null;

/** The backend returns 409 when the scheduler has already claimed the blog and
 *  is publishing it right now — at that point it can no longer be cancelled or
 *  rescheduled. Detected here so we never show a false "cancelled". */
const isAlreadyPublishingError = (e: unknown): boolean =>
  typeof e === "object" &&
  e !== null &&
  (e as { response?: { status?: number } }).response?.status === 409;

const labelClass =
  "block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1";
const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-theme-primary focus:ring-1 focus:ring-theme-primary outline-none";
const hintClass = "mt-1 text-xs text-gray-500";
// A field worth filling in but still empty: a quiet brand cue, not a warning colour.
const emptyCueClass = "border-theme-primary/40 bg-theme-primary/5";

/** Status pills share one shape; only the emphasis changes. */
const pillClass = (kind: "live" | "scheduled" | "muted") =>
  `inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
    kind === "live"
      ? "bg-theme-primary/10 text-theme-primary ring-1 ring-inset ring-theme-primary/25"
      : kind === "scheduled"
      ? "bg-theme-primary/5 text-theme-primary ring-1 ring-inset ring-theme-primary/20"
      : "bg-gray-100 text-gray-500"
  }`;

// Letters in any language, digits, spaces, "_" and "-": the same set the server
// keeps (Python's \w), so the address shown is the address saved.
const NOT_SLUG_CHARS = new RegExp(String.raw`[^\p{L}\p{N}\s_-]`, "gu");

/** The post's web address, as the server saves it: lowercase words joined by dashes. */
export const toSlug = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(NOT_SLUG_CHARS, "")
    .replace(/[-\s]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Where search engines cut the listing off; past these, Google shortens it.
const SEARCH_TITLE_LIMIT = 60;
const SEARCH_DESCRIPTION_LIMIT = 160;

/** The saved description of the post's featured library image. */
const featuredAltOf = (draft?: BlogPost | null): string =>
  (draft?.featuredMediaId && draft.media?.find((m) => m.media_id === draft.featuredMediaId)?.alt) || "";

const isSlugTaken = (e: unknown): e is Error => e instanceof Error && e.name === "SlugTakenError";

// Rotating "what the agent is doing now" lines for the writing state. Ordered to
// read as natural progress; once exhausted we loop the last few "finishing" lines
// so a long generation keeps moving without resetting to "researching".
const WRITING_ACTIVITIES = [
  "Researching your topic",
  "Shaping the outline",
  "Drafting the introduction",
  "Writing the key sections",
  "Adding examples and detail",
  "Working in your call to action",
  "Tightening the wording",
  "Polishing the final draft",
];

// Scoped keyframes for the writing state (this is app UI, not published blog HTML,
// so a <style> block is fine here). Drives the shimmer skeleton, the staggered
// line entrance, the sweeping progress bar, and the rotating-message fade.
const WRITING_ANIM_CSS = `
@keyframes wpblogShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes wpblogFadeUp { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }
@keyframes wpblogBar { 0% { transform: translateX(-120%); } 100% { transform: translateX(440%); } }
@keyframes wpblogMsg { 0% { opacity: 0; transform: translateY(4px); } 14% { opacity: 1; transform: translateY(0); } 86% { opacity: 1; } 100% { opacity: 0; transform: translateY(-4px); } }
.wpblog-skel { background: linear-gradient(90deg,#eef1f4 0%,#e2e6ea 18%,#eef1f4 36%); background-size: 200% 100%; animation: wpblogShimmer 1.6s linear infinite, wpblogFadeUp .5s ease both; }
.wpblog-bar { animation: wpblogBar 1.15s ease-in-out infinite; }
.wpblog-msg { animation: wpblogMsg 3s ease-in-out both; }
`;

export const BlogEditorPanel: React.FC<BlogEditorPanelProps> = ({
  customerId,
  idToken,
  initialDraft,
  connectedSites,
  loading = false,
  streamingPreview = "",
  initialView = "edit",
  onClose,
  onPublished,
  onPersisted,
}) => {
  const [view, setView] = useState<"edit" | "preview">(initialView);

  const [title, setTitle] = useState(initialDraft?.title ?? "");
  const [content, setContent] = useState(initialDraft?.content ?? "");
  const [excerpt, setExcerpt] = useState(initialDraft?.excerpt ?? "");
  // Byline: a saved Author of this Brand (GET /authors). The free-text author
  // fields older posts carry are kept as they are; new saves send author_id.
  const [authorId, setAuthorId] = useState(initialDraft?.authorId ?? "");
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [showNewAuthor, setShowNewAuthor] = useState(false);
  const [newAuthor, setNewAuthor] = useState({ name: "", job_title: "", linkedin: "", bio: "" });
  const [media, setMedia] = useState<BlogMediaRef[]>(initialDraft?.media ?? []);
  const [category, setCategory] = useState(initialDraft?.category ?? "");
  const [featuredImage, setFeaturedImage] = useState(
    initialDraft?.featuredImage ?? ""
  );
  // A library/AI pick keeps its media id, so its description travels with it.
  const [featuredMediaId, setFeaturedMediaId] = useState(initialDraft?.featuredMediaId ?? "");
  const [featuredAlt, setFeaturedAlt] = useState(featuredAltOf(initialDraft));
  const [slug, setSlug] = useState(initialDraft?.slug ?? "");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [metaTitle, setMetaTitle] = useState(initialDraft?.metaTitle ?? "");
  const [metaDescription, setMetaDescription] = useState(initialDraft?.metaDescription ?? "");
  const [tags, setTags] = useState<string[]>(initialDraft?.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [postId, setPostId] = useState<string | null>(initialDraft?.id ?? null);

  // Every place this Brand can publish (WordPress sites + the hosted blog).
  // A post is live at ONE of them at a time (the backend enforces it).
  const [destinations, setDestinations] = useState<BlogDestination[]>([]);
  const [hosted, setHosted] = useState<HostedBlog | null>(null);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedDestId, setSelectedDestId] = useState<string>("");
  const [publications, setPublications] = useState<BlogPublication[]>(initialDraft?.publications ?? []);
  const [showConnect, setShowConnect] = useState(false);
  // The Shopify lookup failed: with nothing else connected, offer a retry
  // instead of the site-builder card (the Brand may publish to Shopify only).
  const [shopifyError, setShopifyError] = useState("");
  const [brandName, setBrandName] = useState("");
  const [brandWebsite, setBrandWebsite] = useState("");
  const [preview, setPreview] = useState<RenderedPreview | null>(null);
  // Content is edited as a document (BlogRichEditor) and stored as Markdown;
  // "Edit as Markdown" stays available for anyone who prefers it.
  const [showMarkdown, setShowMarkdown] = useState(false);
  const richEditorRef = useRef<BlogRichEditorHandle>(null);
  // media_id -> displayable URL, so asset: images show inside the editor.
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
  const [assetsReady, setAssetsReady] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  // The version this editor started from; sent with every save so an editor
  // left open on an older copy can never overwrite newer text.
  const [contentVersion, setContentVersion] = useState<number>(initialDraft?.contentVersion ?? 1);
  const [staleCopy, setStaleCopy] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [busy, setBusy] = useState<BusyAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  // Persisted publish state — seeded from the loaded draft so the button still
  // reads "Published" when the blog is reopened later, not just in this session.
  const [published, setPublished] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // SGL-500: publishing or scheduling a post with no picture first shows a
  // short note in the footer (links shared to Facebook/LinkedIn would show no
  // image). The owner adds one or goes ahead; nothing is generated for them.
  const [pictureCheck, setPictureCheck] = useState<null | "publish" | "schedule">(null);
  const [dirty, setDirty] = useState(false);

  // ── Scheduling ──────────────────────────────────────────────────────────────
  const [status, setStatus] = useState<BlogStatus>(initialDraft?.status ?? "DRAFT");
  // Held as whatever the picker/backend gave us (a local wall-clock value or a
  // UTC ISO string); normalized to a UTC instant only when we send it.
  const [scheduledTime, setScheduledTime] = useState<string>(
    initialDraft?.scheduledTime ?? ""
  );
  const [showSchedule, setShowSchedule] = useState<boolean>(
    initialDraft?.status === "SCHEDULED"
  );
  const isScheduled = status === "SCHEDULED";
  // The scheduler owns the row while it publishes; nothing may be changed.
  const isPublishing = status === "ENQUEUED";
  // Generation progress stage shown while the blog is being written (0/1/2).
  const [genStage, setGenStage] = useState(0);
  // Rotating "activity" line index while the agent is writing.
  const [writingTick, setWritingTick] = useState(0);

  // Image picker: shared by the featured-image control and inline insertion.
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [pickerMode, setPickerMode] = useState<"featured" | "inline" | null>(null);
  // Brand aesthetics for the active business profile, used to keep AI-generated
  // images on-brand. Best-effort: a failure just leaves prompts generic.
  const [brandColors, setBrandColors] = useState<Array<{ hex: string; role: string }>>([]);
  const [brandIndustry, setBrandIndustry] = useState("");
  const [pickerTab, setPickerTab] = useState<"ai" | "library">("ai");

  const openPicker = (
    mode: "featured" | "inline",
    tab: "ai" | "library" = "ai"
  ) => {
    setPickerMode(mode);
    setPickerTab(tab);
  };

  // Tracks the *content* of the draft we last fully loaded, so we can tell a
  // genuinely different draft apart from the same draft being fed back after a
  // save (where only id/published/url change).
  const loadedDraftSigRef = useRef<string | null>(null);

  // Reset editor state when a *different* draft is loaded into the panel. When the
  // parent feeds back the same draft after a Save/Publish (same content, only new
  // id/published/url), we keep the user's in-panel edits — most visibly a
  // just-selected featured image — and only sync the persistence fields, instead
  // of wiping everything and snapping back to the Preview tab.
  useEffect(() => {
    const sig = initialDraft
      ? `${initialDraft.title ?? ""} ${initialDraft.content ?? ""} ${
          initialDraft.excerpt ?? ""
        }`
      : " none";

    if (loadedDraftSigRef.current === sig) {
      setPostId(initialDraft?.id ?? null);
      if (initialDraft?.publications) setPublications(initialDraft.publications);
      setPublished(initialDraft?.published ?? false);
      setPublishedUrl(initialDraft?.wordpressUrl ?? null);
      return;
    }
    loadedDraftSigRef.current = sig;

    setTitle(initialDraft?.title ?? "");
    setContent(
      stripDuplicateTitleHeading(
        initialDraft?.content ?? "",
        initialDraft?.title ?? ""
      )
    );
    setExcerpt(initialDraft?.excerpt ?? "");
    setCategory(initialDraft?.category ?? "");
    setFeaturedImage(initialDraft?.featuredImage ?? "");
    setFeaturedMediaId(initialDraft?.featuredMediaId ?? "");
    setFeaturedAlt(featuredAltOf(initialDraft));
    setSlug(initialDraft?.slug ?? "");
    setSlugError(null);
    setMetaTitle(initialDraft?.metaTitle ?? "");
    setMetaDescription(initialDraft?.metaDescription ?? "");
    setTags(initialDraft?.tags ?? []);
    setAuthorId(initialDraft?.authorId ?? "");
    setMedia(initialDraft?.media ?? []);
    setContentVersion(initialDraft?.contentVersion ?? 1);
    setStaleCopy(false);
    setPublications(initialDraft?.publications ?? []);
    setPostId(initialDraft?.id ?? null);
    setPublished(initialDraft?.published ?? false);
    setPublishedUrl(initialDraft?.wordpressUrl ?? null);
    setDirty(false);
    setError(null);
    setConfirmDelete(false);
    setPictureCheck(null);
    setView(initialView);
  }, [initialDraft, initialView]);

  // Drive the "Outlining → Writing sections → Polishing" indicator while the AI
  // is still writing. Time-based so it advances even before any text streams in;
  // arrival of streamed content bumps it to at least "Writing sections".
  const isWriting = loading && !initialDraft;
  useEffect(() => {
    if (!isWriting) {
      setGenStage(0);
      return;
    }
    const t1 = window.setTimeout(() => setGenStage((s) => Math.max(s, 1)), 2500);
    const t2 = window.setTimeout(() => setGenStage((s) => Math.max(s, 2)), 11000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isWriting]);

  useEffect(() => {
    if ((streamingPreview || "").trim()) setGenStage((s) => Math.max(s, 1));
  }, [streamingPreview]);

  // Cycle the rotating activity line while writing.
  useEffect(() => {
    if (!isWriting) {
      setWritingTick(0);
      return;
    }
    const id = window.setInterval(() => setWritingTick((t) => t + 1), 3000);
    return () => window.clearInterval(id);
  }, [isWriting]);

  // Load every place this Brand can publish to. Re-run after a connection
  // changes (the connect card reports back through setHosted).
  const loadDestinations = React.useCallback(async () => {
    setSitesLoading(true);
    try {
      const res = await listBlogDestinations(customerId, idToken);
      setDestinations(res.destinations);
      setHosted(res.hosted);
      setShopifyError(res.shopifyError || "");
      const lookupErrors = [
        res.hostedError && `Couldn't check your hosted blog: ${res.hostedError}`,
        res.shopifyError && `Couldn't check your Shopify store: ${res.shopifyError}`,
      ].filter(Boolean);
      if (lookupErrors.length) setError(lookupErrors.join(" "));
      setSelectedDestId((cur) => {
        if (cur && res.destinations.some((d) => d.id === cur)) return cur;
        // Default: where this post already lives, else the only ready destination.
        const live = (initialDraft?.publications ?? []).find((p) =>
          ["PUBLISHED", "SCHEDULED", "ENQUEUED", "FAILED"].includes(p.status)
        );
        if (live && res.destinations.some((d) => d.id === live.destinationId)) return live.destinationId;
        const ready = res.destinations.filter((d) => !d.pending);
        return ready.length === 1 ? ready[0].id : "";
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load your sites");
    } finally {
      setSitesLoading(false);
    }
  }, [customerId, idToken, initialDraft?.publications]);

  useEffect(() => {
    void loadDestinations();
  }, [loadDestinations]);

  // The draft handed in (the chat keeps its own copy) can be older than the
  // server's. Read where the post is live now, so the row and banner agree.
  useEffect(() => {
    if (!postId) return;
    let active = true;
    getBlogDraft(customerId, postId, idToken)
      .then((latest) => {
        if (!active || !latest.publications) return;
        setPublications(latest.publications);
        const live = latest.publications.find((p) => p.status === "PUBLISHED" && p.destinationType !== "public_api");
        setPublished(Boolean(live));
        setPublishedUrl(live?.url ?? null);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [postId, customerId, idToken]);

  // Image URLs for the editor (the saved Markdown keeps asset:<media_id>).
  useEffect(() => {
    let active = true;
    setAssetsReady(false);
    fetchMediaByCustomer(customerId, idToken, { mediaType: "image", limit: 200 })
      .then(({ medias }) => {
        if (!active) return;
        const map: Record<string, string> = {};
        medias.filter(isImageMediaItem).forEach((m) => {
          const url = getMediaThumbnailUrl(m);
          if (m.media_id && url) map[m.media_id] = url;
        });
        setAssetUrls(map);
      })
      .catch(() => active && setAssetUrls({}))
      .finally(() => active && setAssetsReady(true));
    return () => {
      active = false;
    };
  }, [customerId, idToken]);
  const resolveImage = React.useCallback(
    (src: string) => (src.startsWith("asset:") ? assetUrls[src.slice(6)] || "" : src),
    [assetUrls]
  );

  // The Brand's saved authors. With none yet, the Brand itself is offered as the
  // author (created on first save), so no post goes out without a byline.
  useEffect(() => {
    let active = true;
    listAuthors(customerId, idToken)
      .then((list) => {
        if (!active) return;
        setAuthors(list);
        setAuthorId((cur) => cur || list.find((a) => a.isDefault)?.id || "");
      })
      .catch(() => active && setAuthors([]));
    return () => {
      active = false;
    };
  }, [customerId, idToken]);

  const titleValid = title.trim().length > 0;
  const contentValid = content.trim().length > 0;
  const canSave = titleValid && contentValid && busy === null;
  const selectedDest = destinations.find((d) => d.id === selectedDestId);
  const canPublish = canSave && Boolean(selectedDest) && !selectedDest?.pending;
  const liveCopies = publications.filter((p) => p.status === "PUBLISHED" && p.destinationType !== "public_api");
  const destLabel = (id: string) => destinations.find((d) => d.id === id)?.label || id.replace(/^wp_/, "WordPress site ");

  // Saved = persisted (has an id) with no unsaved edits. postId is seeded from
  // initialDraft.id, so this stays "Saved" across reopen and blocks duplicate saves.
  const isSaved = Boolean(postId) && !dirty;
  // Published = published to WordPress and unchanged since (seeded from the draft).
  // The live copy shows an older version than what is saved here.
  const liveCopy = publications.find((p) => p.status === "PUBLISHED" && p.destinationType !== "public_api");
  const liveBehind = Boolean(
    liveCopy && typeof liveCopy.publishedVersion === "number" && liveCopy.publishedVersion < contentVersion
  );
  const isPublished = published && !dirty && !liveBehind;

  const addTag = (raw: string) => {
    const next = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!next.length) return;
    setTags((prev) => Array.from(new Set([...prev, ...next])));
    setTagInput("");
    setDirty(true);
  };

  const removeTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
    setDirty(true);
  };

  // Only images still referenced in the content are kept in the media list.
  const usedMedia = (): BlogMediaRef[] =>
    media.filter((m) => content.includes(`asset:${m.media_id}`));

  // The body's images plus the featured image's description (when it has one).
  const mediaWithFeatured = (): BlogMediaRef[] => {
    const list = usedMedia();
    const alt = featuredAlt.trim();
    if (!featuredImage.trim() || !featuredMediaId || !alt) return list;
    return list.some((m) => m.media_id === featuredMediaId)
      ? list.map((m) => (m.media_id === featuredMediaId ? { ...m, alt } : m))
      : [...list, { media_id: featuredMediaId, alt }];
  };

  const buildInput = (aid: string = authorId): BlogDraftInput => ({
    title: title.trim(),
    content: content.trim(),
    excerpt: excerpt.trim(),
    category: category.trim(),
    featured_image: featuredImage.trim(),
    featured_media_id: featuredImage.trim() ? featuredMediaId : "",
    ...(toSlug(slug) ? { slug: toSlug(slug) } : {}),
    meta_title: metaTitle.trim(),
    meta_description: metaDescription.trim(),
    tags,
    // The server renders every destination's HTML from these fields (SGL-66).
    ...(aid ? { author_id: aid, author: authors.find((a) => a.id === aid)?.name ?? brandName } : {}),
    media: mediaWithFeatured(),
  });

  /** With no saved author yet, make the Brand the author before the first save.
   *  Returns the author id to save with. */
  const ensureAuthor = async (): Promise<string> => {
    if (authorId || authors.length || !brandName.trim()) return authorId;
    const created = await createAuthor(customerId, idToken, {
      name: brandName.trim(),
      kind: "organization",
      is_default: true,
    });
    setAuthors([created]);
    setAuthorId(created.id);
    return created.id;
  };

  // Load the brand aesthetics for this customer so the image prompts can stay
  // on-brand. Runs once per customer; failures are non-fatal (generic prompt).
  useEffect(() => {
    if (!customerId || !idToken) return;
    let cancelled = false;
    fetchBrands(customerId, idToken)
      .then(({ brands }) => {
        if (cancelled) return;
        const brand = brands?.[0];
        setBrandName((brand?.brand?.name ?? "").trim());
        setBrandWebsite((brand?.brand?.website_url ?? "").trim());
        const colors = brand?.visual?.colors ?? [];
        setBrandColors(
          colors.filter((c) => Boolean(c?.hex)).map((c) => ({ hex: c.hex, role: c.role }))
        );
        setBrandIndustry((brand?.brand?.industry ?? "").trim());
      })
      .catch(() => {
        if (cancelled) return;
        setBrandColors([]);
        setBrandIndustry("");
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, idToken]);

  // Art-direction color clause: lead with the brand's primary, support with the
  // rest, by role — so the image reads as on-brand. Empty when no colors set.
  const brandPaletteClause = useMemo(() => {
    if (!brandColors.length) return "";
    const primary = brandColors.find((c) => c.role === "primary")?.hex;
    const accents = brandColors.filter((c) => c.hex && c.hex !== primary).map((c) => c.hex);
    if (primary) {
      const accentPart = accents.length ? `, accented by ${accents.join(", ")}` : "";
      return ` Color palette: lead with ${primary} as the dominant tone${accentPart}, kept cohesive and on-brand.`;
    }
    return ` Color palette: ${brandColors.map((c) => c.hex).join(", ")}, kept cohesive and on-brand.`;
  }, [brandColors]);

  // Optional industry context so the imagery is relevant to the business.
  const brandContext = useMemo(
    () => (brandIndustry ? ` for a ${brandIndustry} business` : ""),
    [brandIndustry]
  );

  // Professional, art-directed prompt for the featured/hero image.
  const featuredImagePrompt = useMemo(() => {
    const t = title.trim();
    const subject = t
      ? `Editorial hero image for a blog post titled "${t}"${brandContext}`
      : `Editorial hero image for a business blog post${brandContext}`;
    return (
      `${subject}. Visual style: clean, modern, premium and professional, with a balanced composition, soft natural lighting and generous negative space.` +
      brandPaletteClause +
      ` Sharp, high-quality, magazine-grade. No text, no words, no logos, no watermarks.`
    );
  }, [title, brandContext, brandPaletteClause]);

  // Lighter-weight prompt for inline body images.
  const inlineImagePrompt = useMemo(() => {
    const t = title.trim();
    const subject = t
      ? `Editorial illustrative image for a blog post about "${t}"${brandContext}`
      : `Clean, modern editorial image for a business blog post${brandContext}`;
    return (
      `${subject}. Visual style: clean, modern and professional, with a balanced composition and soft lighting.` +
      brandPaletteClause +
      ` High-quality. No text, no words, no logos, no watermarks.`
    );
  }, [title, brandContext, brandPaletteClause]);

  // Insert an image at the caret (or append). Library/AI images go in as
  // `asset:<media_id>` with alt text in the media list, which every destination
  // (including the hosted blog) accepts; a pasted URL stays a plain link.
  const insertInlineImage = (url: string, info?: { mediaId?: string; alt?: string }) => {
    if (!url) return;
    const ref = info?.mediaId ? `asset:${info.mediaId}` : url;
    if (info?.mediaId) {
      const id = info.mediaId;
      setMedia((prev) => (prev.some((m) => m.media_id === id) ? prev : [...prev, { media_id: id, alt: (info.alt || "").slice(0, 250) }]));
    }
    if (info?.mediaId) setAssetUrls((prev) => ({ ...prev, [info.mediaId as string]: url }));
    if (!showMarkdown && richEditorRef.current) {
      richEditorRef.current.insertImage(ref, (info?.alt || "").replace(/[[\]]/g, ""), url);
      setDirty(true);
      return;
    }
    const snippet = `\n\n![${(info?.alt || "").replace(/[[\]]/g, "")}](${ref})\n\n`;
    const ta = contentRef.current;
    if (ta) {
      const start = ta.selectionStart ?? content.length;
      const end = ta.selectionEnd ?? content.length;
      setContent((prev) => prev.slice(0, start) + snippet + prev.slice(end));
    } else {
      setContent((prev) => prev + snippet);
    }
    setDirty(true);
  };

  const handlePickerSelect = (url: string, info?: { mediaId?: string; alt?: string }) => {
    if (pickerMode === "featured") {
      setFeaturedImage(url);
      setFeaturedMediaId(info?.mediaId ?? "");
      setFeaturedAlt((info?.alt || "").slice(0, 250));
      setDirty(true);
    } else if (pickerMode === "inline") {
      insertInlineImage(url, info);
    }
  };

  const persistDraft = async (): Promise<string> => {
    const input = buildInput(await ensureAuthor());
    if (postId) {
      const res = await updateBlogDraft(customerId, postId, idToken, { ...input, base_version: contentVersion });
      if (typeof res.contentVersion === "number") setContentVersion(res.contentVersion);
      return postId;
    }
    const res = await createBlogDraft(customerId, idToken, input);
    setPostId(res.id);
    setContentVersion(1);
    return res.id;
  };

  const isStale = (e: unknown) => e instanceof Error && e.name === "StaleVersionError";

  /** Replace this editor's copy with the latest saved version (after a refused save). */
  const loadLatest = async () => {
    if (!postId) return;
    try {
      const latest = await getBlogDraft(customerId, postId, idToken);
      setTitle(latest.title ?? "");
      setContent(latest.content ?? "");
      setExcerpt(latest.excerpt ?? "");
      setCategory(latest.category ?? "");
      setFeaturedImage(latest.featuredImage ?? "");
      setFeaturedMediaId(latest.featuredMediaId ?? "");
      setFeaturedAlt(featuredAltOf(latest));
      setSlug(latest.slug ?? "");
      setSlugError(null);
      setMetaTitle(latest.metaTitle ?? "");
      setMetaDescription(latest.metaDescription ?? "");
      setTags(latest.tags ?? []);
      setAuthorId(latest.authorId ?? "");
      setMedia(latest.media ?? []);
      setPublications(latest.publications ?? []);
      setContentVersion(latest.contentVersion ?? 1);
      setDirty(false);
      setStaleCopy(false);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load the latest version");
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setBusy("saving");
    setError(null);
    try {
      const id = await persistDraft();
      setDirty(false);
      onPersisted?.(id, published, publishedUrl ?? undefined);
    } catch (e: unknown) {
      if (isStale(e)) setStaleCopy(true);
      if (isSlugTaken(e)) setSlugError(e.message);
      setError(e instanceof Error ? e.message : "Failed to save draft");
    } finally {
      setBusy(null);
    }
  };

  // Drafts save themselves a moment after the owner stops typing. Live or
  // scheduled posts do not: changing what is live stays a deliberate click.
  useEffect(() => {
    if (!dirty || !postId || busy !== null || published || status !== "DRAFT" || staleCopy) return;
    if (!titleValid || !contentValid) return;
    const t = window.setTimeout(() => {
      void handleSave().then(() => setAutoSaved(true));
    }, 2500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, postId, busy, published, status, title, content, excerpt, category, featuredImage, featuredMediaId,
      featuredAlt, slug, metaTitle, metaDescription, tags, authorId, media]);

  // The same rule the server uses for link previews: the featured image, else
  // the first image in the body.
  const hasPicture = Boolean(featuredImage.trim()) || /!\[[^\]]*\]\([^)\s]+/.test(content);

  // Publish / Confirm schedule go through here: with no picture, ask once first.
  const publishOrAsk = () => (hasPicture ? void handlePublish() : setPictureCheck("publish"));
  const scheduleOrAsk = () => (hasPicture ? void handleSchedule() : setPictureCheck("schedule"));
  const goAheadWithoutPicture = () => {
    const next = pictureCheck;
    setPictureCheck(null);
    if (next === "publish") void handlePublish();
    if (next === "schedule") void handleSchedule();
  };

  const handlePublish = async () => {
    if (!titleValid || !contentValid) {
      setError("A title and content are required before publishing.");
      return;
    }
    // A live post is updated where it lives (one site per post).
    const target = (liveCopy && destinations.find((d) => d.id === liveCopy.destinationId)) || selectedDest;
    if (!target) {
      setError("Choose where to publish it.");
      return;
    }
    setBusy("publishing");
    setError(null);
    try {
      const id = await persistDraft();
      setDirty(false);
      const res = await publishToDestination(customerId, idToken, id, target);
      setPublishedUrl(res.url);
      setPublished(true);
      setStatus("PUBLISHED");
      const latest = await getBlogDraft(customerId, id, idToken).catch(() => null);
      if (latest) {
        setPublications(latest.publications ?? []);
        setContentVersion(latest.contentVersion ?? contentVersion);
      } else {
        setPublications((prev) => [
          ...prev.filter((p) => p.destinationId !== target.id),
          { destinationId: target.id, destinationType: target.type, status: "PUBLISHED", url: res.url, publishedVersion: contentVersion },
        ]);
      }
      onPersisted?.(id, true, res.url);
      onPublished?.(res.url, id);
    } catch (e: unknown) {
      if (isStale(e)) setStaleCopy(true);
      if (isSlugTaken(e)) setSlugError(e.message);
      setError(e instanceof Error ? e.message : "Failed to publish");
    } finally {
      setBusy(null);
    }
  };

  /** Take the post down from one site. The server answers once the page is gone. */
  const handleUnpublish = async (destinationId: string) => {
    if (!postId) return;
    setBusy("unpublishing");
    setError(null);
    try {
      await unpublishFromDestination(customerId, idToken, postId, destinationId);
      const rest = publications.map((p) =>
        p.destinationId === destinationId ? { ...p, status: "UNPUBLISHED" } : p
      );
      setPublications(rest);
      const stillLive = rest.some((p) => p.status === "PUBLISHED" && p.destinationType !== "public_api");
      if (!stillLive) {
        setPublished(false);
        setPublishedUrl(null);
        setStatus("DRAFT");
        onPersisted?.(postId, false, undefined);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to unpublish");
    } finally {
      setBusy(null);
    }
  };

  /**
   * Schedule (or reschedule) this blog. Deliberately does NOT call
   * publishBlogToWordPress — a future-dated blog is written to the DB and the
   * backend cron publishes it, mirroring how social posts defer to their
   * scheduler (see utils/socialPostSchedule.ts).
   */
  const handleSchedule = async () => {
    if (!titleValid || !contentValid) {
      setError("A title and content are required before scheduling.");
      return;
    }
    if (!selectedDest || selectedDest.pending) {
      setError("Choose a connected site to publish to.");
      return;
    }
    if (!scheduledTime) {
      setError("Pick the date and time to publish.");
      return;
    }
    setBusy("scheduling");
    setError(null);
    try {
      const input: BlogDraftInput = {
        ...buildInput(await ensureAuthor()),
        status: "SCHEDULED",
        scheduled_time: normalizeScheduledTime(scheduledTime),
        ...(selectedDest.type === "wordpress"
          ? { wordpress_site_id: selectedDest.wordpressSiteId }
          : { destination_id: selectedDest.id }),
      };
      let id = postId;
      if (id) {
        const r = await updateBlogDraft(customerId, id, idToken, input);
        if (typeof r.contentVersion === "number") setContentVersion(r.contentVersion);
      } else {
        id = (await createBlogDraft(customerId, idToken, input)).id;
        setPostId(id);
      }
      setStatus("SCHEDULED");
      setDirty(false);
      onPersisted?.(id, false, undefined);
    } catch (e: unknown) {
      if (isAlreadyPublishingError(e)) {
        setStatus("ENQUEUED");
        setError(
          "This blog is already being published and can no longer be rescheduled."
        );
      } else {
        if (isSlugTaken(e)) setSlugError(e.message);
        setError(e instanceof Error ? e.message : "Failed to schedule the blog");
      }
    } finally {
      setBusy(null);
    }
  };

  /**
   * Cancel a schedule. The backend clears scheduled_time, which drops the blog
   * out of the scheduler's index entirely.
   *
   * If the scheduler claimed the blog in the meantime it returns 409 and we say
   * so plainly, rather than showing a "cancelled" tick while the post goes live
   * anyway (which is what the social pipeline does today).
   */
  const handleCancelSchedule = async () => {
    if (!postId) return;
    setBusy("cancelling");
    setError(null);
    try {
      const r = await updateBlogDraft(customerId, postId, idToken, {
        ...buildInput(),
        status: "DRAFT",
      });
      if (typeof r.contentVersion === "number") setContentVersion(r.contentVersion);
      setStatus("DRAFT");
      setScheduledTime("");
      setShowSchedule(false);
      onPersisted?.(postId, false, undefined);
    } catch (e: unknown) {
      if (isAlreadyPublishingError(e)) {
        setStatus("ENQUEUED");
        setError(
          "This blog is already being published and can no longer be cancelled."
        );
      } else {
        setError(e instanceof Error ? e.message : "Failed to cancel the schedule");
      }
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!postId) {
      onClose();
      return;
    }
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy("deleting");
    setError(null);
    try {
      await deleteBlogDraft(customerId, postId, idToken);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete draft");
      setBusy(null);
    }
  };

  // Preview = the server renderer, the same one every destination publishes
  // with, so what the owner sees is what goes live (SGL-66 F1).
  useEffect(() => {
    if (view !== "preview" || !content.trim()) {
      if (!content.trim()) setPreview(null);
      return;
    }
    let active = true;
    setPreviewLoading(true);
    const t = window.setTimeout(() => {
      previewDraft(customerId, idToken, {
        title,
        content,
        excerpt,
        featured_image: featuredImage,
        // Sent as on save, so the preview's list of problems (e.g. "No picture
        // yet") sees the same featured image the live page will.
        featured_media_id: featuredImage.trim() ? featuredMediaId : "",
        media: mediaWithFeatured(),
        ...(authorId ? { author_id: authorId } : brandName ? { author: brandName } : {}),
      })
        .then((r) => active && setPreview(r))
        .catch((e: unknown) => active && setPreview({ html: "", problems: [e instanceof Error ? e.message : "Preview failed"] }))
        .finally(() => active && setPreviewLoading(false));
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, title, content, excerpt, featuredImage, featuredMediaId, featuredAlt, media, authorId, brandName,
      customerId, idToken]);
  const previewHtml = preview?.html ?? "";

  // While the AI is still writing the blog, show a clear "Writing…" state.
  if (loading && !initialDraft) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-theme-border bg-theme-surface">
        <header className="relative flex-shrink-0 border-b border-theme-border px-6 pb-4 pt-6">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] animate-pulse bg-gradient-to-r from-theme-primary/40 via-theme-primary to-theme-primary/40" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-sans text-xl font-bold tracking-tight text-theme-fg">
                Blog Post
              </h2>
              <p className="mt-1 text-xs font-medium text-theme-fg-muted">
                Writing your blog from the chat…
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-theme-fg-muted transition-colors hover:bg-theme-background hover:text-theme-fg"
              aria-label="Close blog panel"
            >
              <span className="material-icons text-[22px]">close</span>
            </button>
          </div>
        </header>
        <div className="custom-scrollbar relative min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <style>{WRITING_ANIM_CSS}</style>

          {/* Indeterminate sweep bar — constant motion reads as "actively working". */}
          <div className="relative mb-5 h-1 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="wpblog-bar absolute inset-y-0 left-0 w-1/4 rounded-full bg-theme-primary" />
          </div>

          {/* Staged checklist. */}
          <ol className="mb-3 space-y-2.5">
            {["Outlining", "Writing sections", "Polishing"].map((label, i) => {
              const done = i < genStage;
              const active = i === genStage;
              return (
                <li key={label} className="flex items-center gap-3 text-sm">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                      done
                        ? "bg-theme-primary text-white"
                        : active
                        ? "border-2 border-theme-primary text-theme-primary"
                        : "border-2 border-gray-200 text-gray-300"
                    }`}
                  >
                    {done ? <span className="material-icons text-[13px]" aria-hidden>check</span> : active ? (
                      <span className="h-2 w-2 animate-pulse rounded-full bg-theme-primary" />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span
                    className={
                      active
                        ? "font-semibold text-theme-fg"
                        : done
                        ? "text-theme-fg-muted"
                        : "text-gray-400"
                    }
                  >
                    {label}
                    {active ? "…" : ""}
                  </span>
                </li>
              );
            })}
          </ol>

          {/* Rotating activity line — varied phrasing makes it feel like real work. */}
          <div className="mb-5 flex items-center gap-2 pl-8 text-xs text-theme-fg-muted">
            <span className="material-icons text-[14px] text-theme-primary">auto_awesome</span>
            <span key={writingTick} className="wpblog-msg">
              {(() => {
                const n = WRITING_ACTIVITIES.length;
                const idx = writingTick < n ? writingTick : n - 3 + (writingTick % 3);
                return WRITING_ACTIVITIES[idx];
              })()}
              …
            </span>
          </div>

          {streamingPreview && streamingPreview.trim() ? (
            // Live: render the blog as it streams in, with a blinking caret.
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <MarkdownRenderer content={streamingPreview} />
              <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-theme-primary align-middle" />
            </div>
          ) : (
            // No text yet: a blog-shaped shimmer skeleton that fades in line-by-line,
            // so it reads as a draft taking shape rather than a frozen placeholder.
            <div className="space-y-2.5" aria-hidden="true">
              <div className="wpblog-skel h-7 w-4/5 rounded-md" style={{ animationDelay: "0ms" }} />
              <div className="h-1.5" />
              <div className="wpblog-skel h-3.5 w-full rounded" style={{ animationDelay: "80ms" }} />
              <div className="wpblog-skel h-3.5 w-11/12 rounded" style={{ animationDelay: "140ms" }} />
              <div className="wpblog-skel h-3.5 w-10/12 rounded" style={{ animationDelay: "200ms" }} />
              <div className="h-2.5" />
              <div className="wpblog-skel h-5 w-1/2 rounded" style={{ animationDelay: "260ms" }} />
              <div className="wpblog-skel h-3.5 w-full rounded" style={{ animationDelay: "320ms" }} />
              <div className="wpblog-skel h-3.5 w-9/12 rounded" style={{ animationDelay: "380ms" }} />
              {[0, 1, 2].map((b) => (
                <div key={b} className="flex items-center gap-2">
                  <span
                    className="wpblog-skel h-2 w-2 shrink-0 rounded-full"
                    style={{ animationDelay: `${440 + b * 70}ms` }}
                  />
                  <span
                    className="wpblog-skel h-3.5 flex-1 rounded"
                    style={{ animationDelay: `${440 + b * 70}ms` }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-theme-border bg-theme-surface">
      {/* Header */}
      <header className="relative flex-shrink-0 border-b border-theme-border px-6 pb-4 pt-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-theme-border via-theme-primary/25 to-transparent" />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-sans text-xl font-bold tracking-tight text-theme-fg">
                Blog Post
              </h2>
              {liveCopies.length > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-theme-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-theme-primary">
                  Live
                </span>
              )}
            </div>
            <p className="mt-1 text-xs font-medium text-theme-fg-muted">
              Edit · preview · publish to your website
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-theme-fg-muted transition-colors hover:bg-theme-background hover:text-theme-fg"
            title="Close panel"
            aria-label="Close blog panel"
          >
            <span className="material-icons text-[22px]">close</span>
          </button>
        </div>

        {/* Edit / Preview toggle */}
        <div className="mt-4 inline-flex rounded-lg bg-gray-100 p-1">
          {(["edit", "preview"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-md px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                view === v
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </header>

      {/* Scrollable body */}
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {liveCopy && (liveCopy.url || publishedUrl) && (
          <PublishedPostBanner
            url={liveCopy.url || publishedUrl || ""}
            siteLabel={destLabel(liveCopy.destinationId) || undefined}
            className="mb-4"
          />
        )}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
            {staleCopy && (
              <button type="button" onClick={loadLatest} className="ml-2 font-semibold underline">
                Load the latest version
              </button>
            )}
          </div>
        )}
        {liveCopy && (liveBehind || dirty) && !staleCopy && (
          <div className="mb-4 rounded-lg border border-theme-primary/20 bg-theme-primary/5 px-4 py-3 text-sm text-theme-fg">
            You changed this after publishing. The live page still shows the earlier version until you click
            <strong> Update live page</strong>.
          </div>
        )}

        {/* Where it goes. One site at a time; each live copy can be taken down. */}
        <div className="mb-5">
          <label className={labelClass}>Publish to</label>
          {sitesLoading && destinations.length === 0 ? (
            <div className="text-sm text-gray-500">Loading your sites…</div>
          ) : destinations.length === 0 && shopifyError && !showConnect ? (
            <div className="rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-600">
              <p>We couldn't load all your sites, so we can't show where this can go yet.</p>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  void loadDestinations();
                }}
                disabled={sitesLoading}
                className="mt-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {sitesLoading ? "Checking…" : "Try again"}
              </button>
            </div>
          ) : destinations.length === 0 || showConnect ? (
            <>
              <ConnectBlogCard
                customerId={customerId}
                idToken={idToken}
                businessName={brandName}
                websiteUrl={brandWebsite}
                initial={hosted}
                onChange={(h) => {
                  setHosted(h);
                  if (h.status === "CONNECTED") {
                    setShowConnect(false);
                    void loadDestinations();
                  }
                }}
              />
              <p className="mt-2 text-xs text-gray-500">
                On WordPress? Connect it under Connections and it shows up here.
              </p>
            </>
          ) : (
            <div className="space-y-2">
              {destinations.map((d) => {
                const pub = publications.find((p) => p.destinationId === d.id);
                const isLive = pub?.status === "PUBLISHED";
                const isSched = pub?.status === "SCHEDULED";
                return (
                  <label
                    key={d.id}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                      selectedDestId === d.id ? "border-theme-primary bg-theme-primary/5" : "border-gray-200"
                    } ${d.pending ? "opacity-70" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="blog-destination"
                      checked={selectedDestId === d.id}
                      disabled={d.pending}
                      onChange={() => setSelectedDestId(d.id)}
                      className="h-4 w-4"
                      style={{ accentColor: "#2f4941" }}
                    />
                    <span className="min-w-0 flex-1 truncate text-gray-800">
                      {d.label}
                      <span className="ml-1.5 text-xs text-gray-400">{d.type === "wordpress" ? "WordPress" : d.type === "shopify" ? "Shopify" : "Your blog"}</span>
                    </span>
                    {d.pending ? (
                      <span className={pillClass("muted")}>{d.pendingLabel || "Not ready yet"}</span>
                    ) : isLive ? (
                      <>
                        <span className={pillClass("live")}>
                          <span className="h-1.5 w-1.5 rounded-full bg-theme-primary" aria-hidden />
                          Live
                        </span>
                        {pub?.url && (
                          <a
                            href={pub.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-xs font-semibold text-theme-primary hover:underline"
                          >
                            View
                            <span className="material-icons text-[13px]" aria-hidden>open_in_new</span>
                          </a>
                        )}
                      </>
                    ) : isSched ? (
                      <span className={pillClass("scheduled")}>Scheduled</span>
                    ) : (
                      <span className={pillClass("muted")}>Not published</span>
                    )}
                  </label>
                );
              })}
              {/* Not for a Shopify store: its blog is the store's own, no second site needed. */}
              {!hosted?.connected && !destinations.some((d) => d.type === "shopify") && (
                <button
                  type="button"
                  onClick={() => setShowConnect(true)}
                  className="text-xs font-semibold text-theme-primary"
                >
                  + Connect a Squarespace, Wix or other site
                </button>
              )}
              {liveCopies.length > 0 && selectedDestId && !liveCopies.some((p) => p.destinationId === selectedDestId) && (
                <p className="text-xs text-theme-primary">
                  It's live on {destLabel(liveCopies[0].destinationId)}. Unpublish it first: a post lives on one site at a time.
                </p>
              )}
            </div>
          )}
        </div>

        {view === "edit" ? (
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
                placeholder="Your blog post title"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="blog-slug">Web address</label>
              <div
                className={`flex items-center rounded-lg border bg-white focus-within:border-theme-primary focus-within:ring-1 focus-within:ring-theme-primary ${
                  slugError ? "border-red-300" : "border-gray-200"
                }`}
              >
                <span className="select-none pl-3 text-sm text-gray-400">/</span>
                <input
                  id="blog-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugError(null);
                    setDirty(true);
                  }}
                  onBlur={() => slug && setSlug(toSlug(slug))}
                  placeholder={toSlug(title) || "your-post-title"}
                  aria-invalid={Boolean(slugError)}
                  aria-describedby="blog-slug-hint"
                  className="block w-full min-w-0 rounded-lg border-0 bg-transparent py-2 pl-1 pr-3 text-sm text-gray-800 shadow-none outline-none focus:ring-0"
                />
              </div>
              <p id="blog-slug-hint" className={slugError ? "mt-1 text-xs text-red-600" : hintClass}>
                {slugError ||
                  (liveCopy
                    ? "Changing it moves the live post. Anyone using the old link is sent to the new one."
                    : "Leave it blank to use the title.")}
              </p>
            </div>

            <div>
              <label className={labelClass}>Excerpt</label>
              <textarea
                value={excerpt}
                onChange={(e) => {
                  setExcerpt(e.target.value);
                  setDirty(true);
                }}
                placeholder="A short summary shown in blog listings and previews."
                rows={2}
                className={`${inputClass} resize-y`}
              />
            </div>

            <div>
              <label className={labelClass}>Featured image</label>
              {featuredImage.trim() ? (
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <img
                    src={featuredImage.trim()}
                    alt="Featured"
                    className="h-20 w-32 shrink-0 rounded-md border border-gray-200 object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <p className="text-xs text-gray-500">
                      Shown as the hero image at the top of your post.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openPicker("featured")}
                        className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFeaturedImage("");
                          setFeaturedMediaId("");
                          setFeaturedAlt("");
                          setDirty(true);
                        }}
                        className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 p-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openPicker("featured", "ai")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
                    >
                      <span className="material-icons text-[16px]">auto_awesome</span>
                      Generate with AI
                    </button>
                    <button
                      type="button"
                      onClick={() => openPicker("featured", "library")}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <span className="material-icons text-[16px]">photo_library</span>
                      Choose from library
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">
                    Adds a hero image to the top of your published post.
                  </p>
                </div>
              )}
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                  or paste an image URL
                </summary>
                <input
                  type="url"
                  value={featuredImage}
                  onChange={(e) => {
                    setFeaturedImage(e.target.value);
                    setFeaturedMediaId("");
                    setFeaturedAlt("");
                    setDirty(true);
                  }}
                  placeholder="https://…/image.jpg"
                  className={`${inputClass} mt-2`}
                />
              </details>
              {featuredImage.trim() && featuredMediaId && (
                <div className="mt-3">
                  <label className={labelClass} htmlFor="blog-featured-alt">Image description</label>
                  <input
                    id="blog-featured-alt"
                    type="text"
                    value={featuredAlt}
                    maxLength={250}
                    onChange={(e) => {
                      setFeaturedAlt(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="e.g. Technician steam-cleaning a living-room carpet"
                    aria-describedby="blog-featured-alt-hint"
                    className={`${inputClass} ${featuredAlt.trim() ? "" : emptyCueClass}`}
                  />
                  <p id="blog-featured-alt-hint" className={hintClass}>
                    Say what the picture shows. Google, AI search and screen readers read this. Left blank, the title is used.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className={labelClass}>Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setDirty(true);
                }}
                placeholder="e.g. Marketing"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="blog-author">Author</label>
              {authors.length === 0 ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  {brandName ? <><strong>{brandName}</strong> (your business) until you add a person.</> : "Your business, until you add a person."}
                </p>
              ) : (
                <select
                  id="blog-author"
                  value={authorId}
                  onChange={(e) => {
                    if (e.target.value === "__new") {
                      setShowNewAuthor(true);
                      return;
                    }
                    setAuthorId(e.target.value);
                    setDirty(true);
                  }}
                  className={inputClass}
                >
                  {!authorId && <option value="">Choose an author</option>}
                  {authors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.kind === "organization" ? " (business)" : a.jobTitle ? `, ${a.jobTitle}` : ""}
                      {a.isDefault ? " · default" : ""}
                    </option>
                  ))}
                  <option value="__new">+ Add a person…</option>
                </select>
              )}
              {authors.length === 0 && !showNewAuthor && (
                <button type="button" onClick={() => setShowNewAuthor(true)} className="mt-1.5 text-xs font-semibold text-theme-primary">
                  + Add a person (name, title, LinkedIn)
                </button>
              )}
              {showNewAuthor && (
                <div className="mt-2 space-y-2 rounded-lg border border-gray-200 p-3">
                  <input className={inputClass} placeholder="Name" value={newAuthor.name}
                    onChange={(e) => setNewAuthor({ ...newAuthor, name: e.target.value })} />
                  <input className={inputClass} placeholder="Title (e.g. Owner, Master Plumber)" value={newAuthor.job_title}
                    onChange={(e) => setNewAuthor({ ...newAuthor, job_title: e.target.value })} />
                  <input className={inputClass} type="url" placeholder="LinkedIn URL (optional)" value={newAuthor.linkedin}
                    onChange={(e) => setNewAuthor({ ...newAuthor, linkedin: e.target.value })} />
                  <textarea className={`${inputClass} resize-y`} rows={2} placeholder="Short bio (optional)" value={newAuthor.bio}
                    onChange={(e) => setNewAuthor({ ...newAuthor, bio: e.target.value })} />
                  <p className="text-[11px] text-gray-500">Saved once for this business and reused on every post. Google and AI search use it to judge expertise.</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!newAuthor.name.trim()}
                      onClick={async () => {
                        try {
                          const created = await createAuthor(customerId, idToken, {
                            name: newAuthor.name.trim(),
                            kind: "person",
                            ...(newAuthor.job_title.trim() ? { job_title: newAuthor.job_title.trim() } : {}),
                            ...(newAuthor.linkedin.trim() ? { linkedin: newAuthor.linkedin.trim() } : {}),
                            ...(newAuthor.bio.trim() ? { bio: newAuthor.bio.trim() } : {}),
                          });
                          setAuthors((prev) => [...prev, created]);
                          setAuthorId(created.id);
                          setShowNewAuthor(false);
                          setNewAuthor({ name: "", job_title: "", linkedin: "", bio: "" });
                          setDirty(true);
                        } catch (e: unknown) {
                          setError(e instanceof Error ? e.message : "Failed to save the author");
                        }
                      }}
                      className="rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    >
                      Save author
                    </button>
                    <button type="button" onClick={() => setShowNewAuthor(false)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className={labelClass}>Tags</label>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-theme-primary/10 px-2.5 py-1 text-xs font-medium text-theme-primary"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="flex text-theme-primary/70 hover:text-theme-primary"
                      aria-label={`Remove ${tag}`}
                    >
                      <span className="material-icons text-[14px]" aria-hidden>close</span>
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTag(tagInput);
                  }
                }}
                onBlur={() => tagInput && addTag(tagInput)}
                placeholder="Type a tag and press Enter"
                className={inputClass}
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className={`${labelClass} mb-0`}>Content</label>
                <div className="flex items-center gap-1">
                  <span className="mr-1 text-[11px] text-gray-400">Insert image:</span>
                  <button
                    type="button"
                    onClick={() => openPicker("inline", "ai")}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <span className="material-icons text-[14px]">auto_awesome</span>
                    AI
                  </button>
                  <button
                    type="button"
                    onClick={() => openPicker("inline", "library")}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <span className="material-icons text-[14px]">photo_library</span>
                    Library
                  </button>
                </div>
              </div>
              {showMarkdown ? (
                <textarea
                  ref={contentRef}
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="Write your blog post in Markdown…"
                  rows={16}
                  className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
                />
              ) : assetsReady ? (
                <BlogRichEditor
                  ref={richEditorRef}
                  value={content}
                  resolveImage={resolveImage}
                  onChange={(md) => {
                    setContent(md);
                    setDirty(true);
                  }}
                />
              ) : (
                <div className="rounded-lg border border-gray-200 px-4 py-6 text-sm text-gray-400">Loading the editor…</div>
              )}
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-gray-400">
                  {autoSaved && isSaved ? "Saved automatically" : ""}
                </span>
                <button
                  type="button"
                  onClick={() => setShowMarkdown((v) => !v)}
                  className="text-[11px] font-semibold text-gray-400 hover:text-gray-600"
                >
                  {showMarkdown ? "Back to the editor" : "Edit as Markdown"}
                </button>
              </div>
            </div>

            {usedMedia().length > 0 && (
              <div>
                <label className={labelClass}>Image descriptions</label>
                <p className="mb-2 text-xs text-gray-500">
                  Say what each picture shows. Google, AI search and screen readers read this.
                </p>
                <div className="space-y-2">
                  {usedMedia().map((m, i) => (
                    <div key={m.media_id} className="flex items-center gap-3">
                      {assetUrls[m.media_id] ? (
                        <img
                          src={assetUrls[m.media_id]}
                          alt=""
                          className="h-10 w-14 shrink-0 rounded-md border border-gray-200 object-cover"
                        />
                      ) : (
                        <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-400">
                          <span className="material-icons text-[18px]" aria-hidden>image</span>
                        </span>
                      )}
                      <input
                        type="text"
                        value={m.alt}
                        maxLength={250}
                        aria-label={`Description of image ${i + 1}`}
                        onChange={(e) => {
                          const alt = e.target.value;
                          setMedia((prev) => prev.map((x) => (x.media_id === m.media_id ? { ...x, alt } : x)));
                          setDirty(true);
                        }}
                        placeholder={`What image ${i + 1} shows`}
                        className={`${inputClass} ${m.alt.trim() ? "" : emptyCueClass}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelClass}>Search listing</label>
              <p className="mb-2 text-xs text-gray-500">
                How this post shows up on Google. Leave both blank to use the title and excerpt.
              </p>
              <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5" aria-hidden="true">
                <p className="truncate text-[15px] leading-snug text-[#1a0dab]">
                  {metaTitle.trim() || title.trim() || "Your blog post title"}
                </p>
                <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-600">
                  {metaDescription.trim() || excerpt.trim() || "A short summary of the post."}
                </p>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="mb-1 flex items-baseline justify-between">
                    <label className={`${labelClass} mb-0`} htmlFor="blog-meta-title">Search title</label>
                    <span
                      className={`text-[11px] tabular-nums ${
                        metaTitle.trim().length > SEARCH_TITLE_LIMIT ? "font-semibold text-theme-primary" : "text-gray-400"
                      }`}
                    >
                      {metaTitle.trim().length}/{SEARCH_TITLE_LIMIT}
                    </span>
                  </div>
                  <input
                    id="blog-meta-title"
                    type="text"
                    value={metaTitle}
                    maxLength={200}
                    onChange={(e) => {
                      setMetaTitle(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="Same as the post title"
                    className={inputClass}
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-baseline justify-between">
                    <label className={`${labelClass} mb-0`} htmlFor="blog-meta-description">Search description</label>
                    <span
                      className={`text-[11px] tabular-nums ${
                        metaDescription.trim().length > SEARCH_DESCRIPTION_LIMIT
                          ? "font-semibold text-theme-primary"
                          : "text-gray-400"
                      }`}
                    >
                      {metaDescription.trim().length}/{SEARCH_DESCRIPTION_LIMIT}
                    </span>
                  </div>
                  <textarea
                    id="blog-meta-description"
                    value={metaDescription}
                    maxLength={500}
                    onChange={(e) => {
                      setMetaDescription(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="Same as the excerpt"
                    rows={2}
                    className={`${inputClass} resize-y`}
                  />
                  {(metaTitle.trim().length > SEARCH_TITLE_LIMIT ||
                    metaDescription.trim().length > SEARCH_DESCRIPTION_LIMIT) && (
                    <p className="mt-1 text-xs text-theme-primary">Google cuts off anything past the count shown.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="rounded-lg border border-gray-200 p-6 shadow-sm"
            style={{ backgroundColor: "#ffffff", color: "#1f2328" }}
          >
            {/*
              True WYSIWYG "paper" sheet: forced white via inline style (NOT the
              `bg-white` class, which the app's dark mode remaps to a dark
              surface). The Journal HTML uses fixed dark text and the published
              WordPress page is light — so the preview shows a light page too,
              readable on the dark app chrome like a document editor's preview.
            */}
            {featuredImage.trim() && (
              <img
                src={featuredImage.trim()}
                alt=""
                className="mb-6 max-h-72 w-full rounded-lg object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            {preview && preview.problems.length > 0 && (
              // Same card as the footer's no-picture note: theme colours only, a
              // calm check-list rather than a warning banner. "Check", not "Fix":
              // some items (no picture yet) are advice and never block publishing.
              <div className="mb-4 flex gap-3 rounded-xl border border-theme-border bg-theme-surface p-3">
                <span className="material-icons mt-0.5 text-[20px] text-theme-primary" aria-hidden="true">
                  fact_check
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-theme-fg">Check before publishing</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-theme-fg-muted marker:text-theme-primary">
                    {preview.problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {previewLoading && !previewHtml ? (
              <p className="text-sm italic text-gray-400">Building the preview…</p>
            ) : previewHtml ? (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <p className="text-sm italic text-gray-400">
                Nothing to preview yet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <footer className="flex-shrink-0 border-t border-theme-border px-6 py-4">
        {/* Scheduling. A scheduled blog is NOT published now — it is written to the
            DB and the backend cron publishes it when its time arrives. */}
        {(showSchedule || isScheduled || isPublishing) && !isPublished && (
          <div className="mb-3 rounded-xl border border-theme-border bg-theme-surface p-3">
            {isPublishing ? (
              <p className="text-sm font-medium text-theme-fg">
                Publishing now — this blog can no longer be cancelled or
                rescheduled.
              </p>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className={labelClass} htmlFor="blog-schedule-at">
                    {isScheduled ? "Scheduled for" : "Publish at"}
                  </label>
                  {isScheduled && (
                    <span className={pillClass("scheduled")}>Scheduled</span>
                  )}
                </div>

                <ScheduleDateTimeField
                  id="blog-schedule-at"
                  value={scheduledTime}
                  onChange={(v) => {
                    setScheduledTime(v);
                    setDirty(true);
                  }}
                  minNow
                  disabled={busy !== null}
                />

                {/* Whose clock the user just picked. The social scheduler never
                    surfaces this, which is how an agency in one timezone can
                    silently schedule for the wrong moment in a client's. */}
                {scheduledTime && (
                  <p className="mt-1.5 text-xs text-theme-fg-muted">
                    Publishes {formatScheduledForDisplay(scheduledTime)}
                  </p>
                )}
                {(!selectedDest || selectedDest.pending) && (
                  <p className="mt-1.5 text-xs text-theme-primary">
                    Choose a connected site above to schedule.
                  </p>
                )}

                <div className="mt-2.5 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={scheduleOrAsk}
                    disabled={!canPublish || !scheduledTime}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy === "scheduling"
                      ? "Scheduling…"
                      : isScheduled
                      ? "Update time"
                      : "Confirm schedule"}
                  </button>

                  {isScheduled ? (
                    <button
                      type="button"
                      onClick={handleCancelSchedule}
                      disabled={busy !== null}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-700 underline hover:bg-red-50 disabled:opacity-50"
                    >
                      {busy === "cancelling" ? "Cancelling…" : "Cancel schedule"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowSchedule(false)}
                      disabled={busy !== null}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-theme-fg-muted hover:bg-theme-secondary/10 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* A note, not an error: same card as scheduling, theme colours only.
            Hidden as soon as the post has a picture. */}
        {pictureCheck && !hasPicture && (
          <div
            role="status"
            aria-live="polite"
            className="mb-3 flex gap-3 rounded-xl border border-theme-border bg-theme-surface p-3"
          >
            <span className="material-icons mt-0.5 text-[20px] text-theme-primary" aria-hidden="true">
              image
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-theme-fg">This post has no picture</p>
              <p className="mt-0.5 text-xs text-theme-fg-muted">
                When it's shared on Facebook, LinkedIn or in a message, the preview will show no image.
                Add one now, or {pictureCheck === "schedule" ? "schedule" : "publish"} without it.
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  autoFocus
                  onClick={() => openPicker("featured", "library")}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add a picture
                </button>
                <button
                  type="button"
                  onClick={goAheadWithoutPicture}
                  disabled={busy !== null}
                  className="rounded-lg border border-theme-border px-3 py-1.5 text-xs font-semibold text-theme-fg transition-all hover:bg-theme-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pictureCheck === "schedule" ? "Schedule anyway" : "Publish anyway"}
                </button>
                <button
                  type="button"
                  onClick={() => setPictureCheck(null)}
                  disabled={busy !== null}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-theme-fg-muted hover:bg-theme-secondary/10 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "FAILED" && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-semibold text-red-800">
              Scheduled publish failed
            </p>
            <p className="mt-0.5 text-xs text-red-700">
              {initialDraft?.publishError ||
                "The site rejected the post. Check the connection and try again."}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <DeleteIconButton
            onClick={handleDelete}
            disabled={busy === "saving" || busy === "publishing"}
            busy={busy === "deleting"}
            active={confirmDelete}
            title={
              confirmDelete
                ? "Click again to confirm delete"
                : postId
                ? "Delete blog"
                : "Discard draft"
            }
          />

          <div className="flex items-center gap-2">
            {/* Save & Publish mirror the "Open Canvas" pill exactly (theme-primary,
                bold white text, soft shadow). The Saved/Published done-state stays
                fully vibrant — only the label changes; we dim ONLY when the action
                is genuinely unavailable (missing title/content), not when done. */}
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || isSaved}
              className={`inline-flex items-center gap-1.5 rounded-xl bg-theme-primary px-3 py-2 text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 ${
                isSaved
                  ? "cursor-default"
                  : "disabled:cursor-not-allowed disabled:opacity-50"
              }`}
            >
              {busy === "saving" ? (
                "Saving…"
              ) : isSaved ? (
                <>
                  <span className="material-icons text-[16px]" aria-hidden>check</span>
                  Saved
                </>
              ) : (
                "Save draft"
              )}
            </button>
            {liveCopy && (
              <button
                type="button"
                onClick={() => void handleUnpublish(liveCopy.destinationId)}
                disabled={busy !== null}
                className="inline-flex items-center gap-1.5 rounded-xl border border-theme-border px-3 py-2 text-sm font-bold text-theme-fg transition-all hover:bg-theme-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
                title={`Take this post off ${destLabel(liveCopy.destinationId)}. You can publish it again later.`}
              >
                {busy === "unpublishing" ? "Unpublishing…" : "Unpublish"}
              </button>
            )}
            {/* Opens the scheduling panel above. Hidden once the blog is live or
                already scheduled (the panel itself then owns the interaction). */}
            {!isPublished && !showSchedule && !isScheduled && !isPublishing && (
              <button
                type="button"
                onClick={() => setShowSchedule(true)}
                disabled={busy !== null}
                className="inline-flex items-center gap-1.5 rounded-xl border border-theme-border px-3 py-2 text-sm font-bold text-theme-fg transition-all hover:bg-theme-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
                title="Publish this blog later"
              >
                Schedule
              </button>
            )}
            <button
              type="button"
              onClick={publishOrAsk}
              disabled={(!canPublish && !liveCopy) || !canSave || isPublished || isPublishing || staleCopy}
              className={`inline-flex items-center gap-2 rounded-xl bg-theme-primary px-3 py-2 text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 ${
                isPublished
                  ? "cursor-default"
                  : "disabled:cursor-not-allowed disabled:opacity-50"
              }`}
            >
              {busy === "publishing" ? (
                <>
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Publishing…
                </>
              ) : isPublished ? (
                <>
                  <span className="material-icons text-[16px]" aria-hidden>check</span>
                  Published
                </>
              ) : liveCopy ? (
                "Update live page"
              ) : (
                "Publish"
              )}
            </button>
          </div>
        </div>
      </footer>

      <BlogImagePicker
        isOpen={pickerMode !== null}
        customerId={customerId}
        idToken={idToken}
        title={pickerMode === "featured" ? "Featured image" : "Insert image"}
        defaultPrompt={
          pickerMode === "featured" ? featuredImagePrompt : inlineImagePrompt
        }
        aspectRatio="16x9"
        initialTab={pickerTab}
        onSelect={handlePickerSelect}
        onClose={() => setPickerMode(null)}
      />
    </div>
  );
};

export default BlogEditorPanel;
